import calculateBlobSHA from '../utils/calculate-blob-sha.js';

/**
 * Request a file upload from the Accounts service.
 *
 * @param {Blob} file The Blob object for the file to be uploaded.
 * @param {String} hash The SHA256 hash of the file to be uploaded.
 * @param {String} authToken The auth token to use when authenticating with Accounts.
 * @param {Object} metadata Optional metadata to include with the uploaded file.
 * @returns Promise
 */
function requestFileUpload(file, hash, authToken, metadata = {}) {
  let { name } = file;

  if (typeof metadata !== "object") {
    return Promise.reject(new Error('Didn\'t pass a valid metadata object'));
  }

  // Make a new File object on the Accounts service with the given credentials and file details.
  return fetch('/api/files/request-post', {
    method: 'POST',
    body: JSON.stringify({ name, hash, meta: metadata }),
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `JWT ${authToken}`
    }
  }).then((response) => response.json()); // Convert the response to JSON before returning.
};

/**
 * Step to upload the file to S3. Takes in the URL to POST to, the fields from Accounts to include
 * in the request, as well as the file blob.
 *
 * @param {any} url
 * @param {any} fields
 * @param {any} file
 * @returns Promise
 */
function uploadToS3(url, fields, file) {
  if (!fields || !url) {
    throw new Error('Accounts didn\'t return valid S3 upload details, bailing...');
  }

  // Compose a form request with the given `fields`.
  let formData = new FormData();
  Object.keys(fields).forEach((field) => {
    formData.append(field, fields[field]);
  });

  // Also include the contents of the file blob.
  formData.append('file', file, file.name);

  // Now, upload to S3!
  return fetch(url, {
    method: 'POST',
    body: formData
  });
}

export default function uploadFile(file, authToken, metadata = {}) {
  // Make sure the file is an instance of a blob.
  if (!file || !(file instanceof Blob)) {
    throw new Error(`Improper file given to uploadAvatar() method, instead was given: ${typeof file} ${file}`);
    return;
  }

  if (!authToken) {
    throw new Error(`No auth token given to uploadFile() method, instead was given: ${authToken}`);
    return;
  }

  if (!(metadata instanceof Object)) {
    throw new Error(`Improper metadata given to uploadFile(), instead was given: ${metadata}`);
    return;
  }

  return new Promise((resolve, reject) => {
    let createdFile = null;

    calculateBlobSHA(file)
      .then((hash) => requestFileUpload(file, hash, authToken, metadata))
      .then((json) => {
        createdFile = json.newfile;
        let fields = json.post && json.post.fields;
        let url = json.post && json.post.url;

        if (fields && url) {
          // File doesn't exist in S3 yet, upload it.
          return uploadToS3(url, fields, file);
        }
      }).then(() => {
        // Now we're good to go! Resolve with the newly created file resource.
        resolve(createdFile);
      }).catch((err) => {
        // Some error happened along the way - simply reject and pass up the error.
        reject(err);
      });
  });
};



// WEBPACK FOOTER //
// ./src/utils/upload-file.js